/**
 * Hero Search auto-tag pills for compound Corporate queries.
 * Sync classification via parseCorporateHeroQuery — blur / Enter / GO.
 *
 * Types: domain | company | location | person
 */

import { findDomainInQuery, isValidDomainHost } from '../pages/home/homeSearchUtils';
import { parseCorporateHeroQuery } from '../pages/home/companyQueryParse';

/** @typedef {'domain' | 'company' | 'location' | 'person'} CorporatePillType */

/** @type {CorporatePillType[]} */
export const CORP_PILL_TYPE_CYCLE = ['domain', 'company', 'location', 'person'];

export const CORP_PILL_TYPE_META = {
  domain: { emoji: '🌐', label: 'Domain', title: 'Website domain' },
  company: { emoji: '🏢', label: 'Company', title: 'Company / brand name' },
  location: { emoji: '📍', label: 'Location', title: 'Location' },
  person: { emoji: '👤', label: 'Person', title: 'Person focus (hybrid DD)' },
};

let pillSeq = 0;

function nextPillId() {
  pillSeq += 1;
  return `cp-${pillSeq}-${Date.now().toString(36)}`;
}

function normalizeWhitespace(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {string} type
 * @returns {CorporatePillType}
 */
export function normalizeCorporatePillType(type) {
  const t = String(type || '').trim().toLowerCase();
  if (t === 'name' || t === 'brand' || t === 'org') return 'company';
  if (t === 'domain' || t === 'company' || t === 'location' || t === 'person') {
    return /** @type {CorporatePillType} */ (t);
  }
  return 'company';
}

/**
 * Materialize when query has a domain and at least one other signal
 * (location, company phrase, or person), or ≥2 tokens with a domain.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function shouldMaterializeCorporatePills(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return false;
  const domain = findDomainInQuery(raw);
  if (!domain || !isValidDomainHost(domain)) return false;
  const tokens = raw.split(/\s+/).filter(Boolean);
  // Domain-only → no pills (nothing to disambiguate).
  if (tokens.length < 2) return false;
  const parsed = parseCorporateHeroQuery(raw);
  const extras =
    (parsed.locationTags?.length || 0) +
    (parsed.companyName && parsed.companyName.toLowerCase() !== domain.toLowerCase() ? 1 : 0) +
    (parsed.personFocus ? 1 : 0);
  return extras >= 1;
}

/**
 * @param {string} text
 * @returns {{ id: string, type: CorporatePillType, text: string }[]}
 */
export function buildCorporatePillsFromQuery(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return [];
  const parsed = parseCorporateHeroQuery(raw);
  /** @type {{ id: string, type: CorporatePillType, text: string }[]} */
  const pills = [];

  if (parsed.personFocus) {
    pills.push({ id: nextPillId(), type: 'person', text: parsed.personFocus });
  }
  if (parsed.domain) {
    pills.push({ id: nextPillId(), type: 'domain', text: parsed.domain });
  }
  for (const loc of parsed.locationTags || []) {
    if (!loc) continue;
    pills.push({ id: nextPillId(), type: 'location', text: loc });
  }
  if (parsed.companyName) {
    const companyText = parsed.companyName;
    const isDomainOnly =
      parsed.domain && companyText.toLowerCase() === parsed.domain.toLowerCase();
    if (!isDomainOnly) {
      pills.push({ id: nextPillId(), type: 'company', text: companyText });
    } else if (!parsed.personFocus && !(parsed.locationTags || []).length) {
      // Domain-only name — still show company pill matching domain for editability.
      pills.push({ id: nextPillId(), type: 'company', text: companyText });
    }
  }

  return pills;
}

/**
 * @param {string} type
 * @returns {CorporatePillType}
 */
export function cycleCorporatePillType(type) {
  const normalized = normalizeCorporatePillType(type);
  const idx = CORP_PILL_TYPE_CYCLE.indexOf(normalized);
  if (idx < 0) return 'company';
  return CORP_PILL_TYPE_CYCLE[(idx + 1) % CORP_PILL_TYPE_CYCLE.length];
}

/**
 * @param {{ id: string, type: string, text: string }[]} pills
 * @param {string} pillId
 */
export function cycleCorporatePillById(pills, pillId) {
  return (pills || []).map((p) => {
    const baseType = normalizeCorporatePillType(p.type);
    if (p.id !== pillId) return { ...p, type: baseType };
    return { ...p, type: cycleCorporatePillType(baseType) };
  });
}

/**
 * @param {{ id: string, type: string, text: string }[]} pills
 * @param {string} pillId
 * @param {string} nextType
 */
export function setCorporatePillTypeById(pills, pillId, nextType) {
  const target = normalizeCorporatePillType(nextType);
  return (pills || []).map((p) => {
    if (p.id !== pillId) return { ...p, type: normalizeCorporatePillType(p.type) };
    return { ...p, type: target };
  });
}

/**
 * @param {{ type: string, text: string }[]} pills
 * @returns {string}
 */
export function composeQueryFromCorporatePills(pills) {
  const list = Array.isArray(pills) ? pills : [];
  const by = (t) =>
    list
      .filter((p) => normalizeCorporatePillType(p.type) === t)
      .map((p) => normalizeWhitespace(p.text))
      .filter(Boolean);

  const person = by('person').join(' ');
  const domain = by('domain').join(' ');
  const locations = by('location');
  const company = by('company').join(' ');
  return normalizeWhitespace([person, domain, ...locations, company].filter(Boolean).join(' '));
}

/**
 * Map pills into corporate tab-storage / launch shape.
 *
 * @param {{ type: string, text: string }[]} pills
 * @returns {{
 *   domain: string,
 *   companyName: string,
 *   personFocus: string,
 *   streamName: string,
 *   locationTags: string[],
 * }}
 */
export function pillsToCorporateTags(pills) {
  const list = Array.isArray(pills) ? pills : [];
  const pick = (t) =>
    list
      .filter((p) => normalizeCorporatePillType(p.type) === t)
      .map((p) => normalizeWhitespace(p.text))
      .filter(Boolean);

  const domain = pick('domain')[0] || '';
  const companyName = pick('company').join(' ') || domain;
  const personFocus = pick('person').join(' ');
  const locationTags = pick('location');
  const streamName =
    personFocus && companyName ? `${companyName} | ${personFocus}` : companyName;

  return {
    domain,
    companyName,
    personFocus,
    streamName,
    locationTags,
  };
}

/**
 * @param {{ id: string }[]} pills
 * @param {string} pillId
 */
export function removeCorporatePillById(pills, pillId) {
  return (pills || []).filter((p) => p.id !== pillId);
}
